/**
 * Type Registry
 *
 * Maps API `__type` values to their formatter, Redux store key, ID extractor,
 * and flatten configuration. This drives the auto-normalizer: when a response
 * object carries `__type`, the registry tells us how to format it, where to
 * store it, and which nested entities to extract.
 */

import {
  formatUser,
  formatSpace,
  formatSpaceMember,
  formatKit,
  formatKitMember,
  formatKitVersion,
  formatSection,
  formatItem,
  formatAsset,
  formatInvitation,
  formatCustomField,
  formatPortal,
  formatPortalItem,
  formatPortalMember,
  formatAssetView,
  formatApiToken,
  formatCharge,
  formatProductType,
  formatField,
  formatMarket,
  formatProduct,
  formatPreset,
  formatSpacePreview,
} from "./entityFormatters";
import camelize from "../helpers/camelize";
import { AnyObject } from "../models";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Describes which nested properties should be extracted into entity buckets. */
export interface FlattenConfig {
  [key: string]: string | FlattenConfig;
}

export type TypeRegistryEntry = {
  /** Transforms raw API data into the Redux-ready shape. */
  formatter: (data: unknown) => unknown;
  /** The key in `state.entities` where this entity lives (e.g. "kits"). */
  storeKey: string;
  /** Extracts the entity's unique ID from the *formatted* object. */
  getId: (entity: AnyObject) => string;
  /** Which properties hold nested entities that should be flattened out. */
  flatten: FlattenConfig;
};

// ---------------------------------------------------------------------------
// Helpers for simple entity types that just need camelize
// ---------------------------------------------------------------------------

const formatDownloadRequest = (data: unknown) => camelize(data as AnyObject);
const formatDirectLink = (data: unknown) => camelize(data as AnyObject);

// ---------------------------------------------------------------------------
// Registry – keyed by the API's `__type` value
// ---------------------------------------------------------------------------

export const typeRegistry: Record<string, TypeRegistryEntry> = {
  user: {
    formatter: formatUser,
    storeKey: "users",
    getId: e => e.id,
    flatten: { spaces: "spaces" },
  },
  space: {
    formatter: formatSpace,
    storeKey: "spaces",
    getId: e => String(e.id),
    flatten: { fields: "fields", kits: "kits" },
  },
  space_preview: {
    formatter: formatSpacePreview,
    storeKey: null,
    getId: e => e.id,
    flatten: {},
  },
  space_access: {
    formatter: formatSpaceMember,
    storeKey: "spaceMembers",
    getId: e => `${e.spaceId}-${e.userId}`,
    flatten: { user: "users", kitAccess: "kitMembers", portalAccess: "portalMembers" },
  },
  kit: {
    formatter: formatKit,
    storeKey: "kits",
    getId: e => String(e.kitId),
    flatten: { space: "spaces", versions: "kitVersions" },
  },
  kit_access: {
    formatter: formatKitMember,
    storeKey: "kitMembers",
    getId: e => `${e.spaceId}-${e.userId}-${e.kitId}`,
    flatten: { kit: "kits", user: "users" },
  },
  portal_access: {
    formatter: formatPortalMember,
    storeKey: "portalMembers",
    getId: e => `${e.portalId}-${e.userId}`,
    flatten: { portal: "portals", user: "users" },
  },
  version: {
    formatter: formatKitVersion,
    storeKey: "kitVersions",
    getId: e => `${e.kitId}-${e.version}`,
    flatten: { sections: "sections", creator: "users" },
  },
  section: {
    formatter: formatSection,
    storeKey: "sections",
    getId: e => `${e.id}-${e.version}`,
    flatten: { items: "items" },
  },
  item: {
    formatter: formatItem,
    storeKey: "items",
    getId: e => `${e.id}-${e.version}`,
    flatten: {
      section: "sections",
      kit: "kits",
      kitVersion: "kitVersions",
      space: "spaces",
      asset: "assets",
      item: "items",
      items: "items",
    },
  },
  asset: {
    formatter: formatAsset,
    storeKey: "assets",
    getId: e => String(e.id),
    flatten: { items: "items", uploader: "users" },
  },
  invitation: {
    formatter: formatInvitation,
    storeKey: "invitations",
    getId: e => String(e.id),
    flatten: {},
  },
  field: {
    formatter: formatCustomField,
    storeKey: "fields",
    getId: e => String(e.id),
    flatten: { space: "spaces" },
  },
  portal: {
    formatter: formatPortal,
    storeKey: "portals",
    getId: e => String(e.id),
    flatten: { space: "spaces" },
  },
  portal_item: {
    formatter: formatPortalItem,
    storeKey: "portalItems",
    getId: e => String(e.id),
    flatten: { kit: "kits", asset: "assets" },
  },
  portal_member: {
    formatter: formatPortalMember,
    storeKey: "portalMembers",
    getId: e => `${e.portalId}-${e.userId}`,
    flatten: { portal: "portals", user: "users" },
  },
  download_request: {
    formatter: formatDownloadRequest,
    storeKey: "downloadRequests",
    getId: e => String(e.id),
    flatten: {},
  },
  view: {
    formatter: formatAssetView,
    storeKey: "views",
    getId: e => String(e.id),
    flatten: {},
  },
  api_token: {
    formatter: formatApiToken,
    storeKey: "apiTokens",
    getId: e => String(e.token),
    flatten: {},
  },
  direct_link: {
    formatter: formatDirectLink,
    storeKey: "directLinks",
    getId: e => String(e.id),
    flatten: {},
  },
  product_type: {
    formatter: formatProductType,
    storeKey: "productTypes",
    getId: e => String(e.id),
    flatten: {},
  },
  product_type_field: {
    formatter: formatField,
    storeKey: "productFields",
    getId: e => String(e.id),
    flatten: {},
  },
  market: {
    formatter: formatMarket,
    storeKey: "markets",
    getId: e => String(e.id),
    flatten: {},
  },
  product: {
    formatter: formatProduct,
    storeKey: "products",
    getId: e => String(e.uuid),
    flatten: { productType: "productTypes", variants: { assets: "assets" } },
  },
  preset: {
    formatter: formatPreset,
    storeKey: "presets",
    getId: e => String(e.key),
    flatten: {},
  },
  charge: {
    formatter: formatCharge,
    storeKey: "charges",
    getId: e => String(e.id),
    flatten: {},
  },
};

// ---------------------------------------------------------------------------
// Derived lookup: storeKey → { getId, flatten }
//
// Used by the flattener when it encounters a nested formatted entity and
// needs to know how to extract its ID and recursively flatten its children.
// ---------------------------------------------------------------------------

export const storeRegistry: Record<
  string,
  { getId: (e: AnyObject) => string; flatten: FlattenConfig }
> = {};

for (const entry of Object.values(typeRegistry)) {
  storeRegistry[entry.storeKey] = { getId: entry.getId, flatten: entry.flatten };
}
